import React, { useEffect, useRef, useState } from 'react';
import {
  Globe, Search, Image as ImageIcon, Link2, FileText, Zap, X,
  Cpu, Palette, Type, CheckCircle2, AlertCircle, Download, ExternalLink,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ScrapeLiveView — modern live scraping experience.
 * Stage 1 (running): animated progress, live counters, event stream
 * Stage 2 (complete): rich structured report of everything found.
 */
export default function ScrapeLiveView({ url, onClose, onResult }) {
  const [progress, setProgress] = useState(0);
  const [stepLabel, setStepLabel] = useState('Preparing…');
  const [metrics, setMetrics] = useState({
    status: null, size_kb: 0, images: 0, links_internal: 0,
    links_external: 0, scripts: 0, headings: 0, words: 0,
  });
  const [logs, setLogs] = useState([]);
  const [capturedImages, setCapturedImages] = useState([]);
  const [overview, setOverview] = useState(null);
  const [techStack, setTechStack] = useState([]);
  const [palette, setPalette] = useState({ fonts: [], colors: [] });
  const [status, setStatus] = useState('running'); // running | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');
  const logsRef = useRef(null);
  const abortRef = useRef(false);
  const startedRef = useRef(Date.now());

  // Elapsed timer
  useEffect(() => {
    if (status !== 'running') return;
    const id = setInterval(() => setElapsed((Date.now() - startedRef.current) / 1000), 100);
    return () => clearInterval(id);
  }, [status]);

  // Auto-scroll log stream
  useEffect(() => {
    if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight;
  }, [logs]);

  const pushLog = (line) => setLogs(l => [...l.slice(-60), { ts: Date.now(), line }]);

  // Open SSE stream
  useEffect(() => {
    abortRef.current = false;
    startedRef.current = Date.now();
    setStatus('running');
    setProgress(0);
    pushLog(`▸ Initializing scrape agent for ${url}`);

    const run = async () => {
      try {
        const resp = await fetch(`${API}/api/scrape`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ url }),
        });
        if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!abortRef.current) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events (each separated by \n\n)
          let idx;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const chunk = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const evMatch = chunk.match(/event:\s*(.+)/);
            const dataMatch = chunk.match(/data:\s*(.+)/s);
            if (!evMatch || !dataMatch) continue;
            const ev = evMatch[1].trim();
            let data = {};
            try { data = JSON.parse(dataMatch[1]); } catch { continue; }
            handleEvent(ev, data);
          }
        }
      } catch (e) {
        setError(e.message || 'Scrape failed');
        setStatus('error');
      }
    };

    run();
    return () => { abortRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const handleEvent = (ev, data) => {
    if (ev === 'step') {
      setProgress(data.progress || 0);
      setStepLabel(data.label || '');
      pushLog(`● ${data.label}`);
    } else if (ev === 'metric') {
      setMetrics(m => ({ ...m, [data.key]: data.value }));
    } else if (ev === 'overview') {
      setOverview(data);
      pushLog(`◆ Title: "${(data.title || '').slice(0, 60)}"`);
    } else if (ev === 'image') {
      setCapturedImages(arr => [...arr.slice(-50), data]);
    } else if (ev === 'log') {
      pushLog(data.line);
    } else if (ev === 'headings') {
      pushLog(`▸ Captured ${data.items?.length || 0} headings`);
    } else if (ev === 'tech') {
      setTechStack(data.stack || []);
      if (data.stack?.length) pushLog(`⚙ Tech detected: ${data.stack.join(', ')}`);
    } else if (ev === 'palette') {
      setPalette(data);
    } else if (ev === 'complete') {
      setResult(data.summary);
      setStatus('done');
      setProgress(100);
      pushLog(`✓ Done in ${data.elapsed_s}s — ready to inspect`);
      onResult && onResult(data.summary);
    } else if (ev === 'error') {
      setError(data.message || 'Unknown error');
      setStatus('error');
    }
  };

  const exportJSON = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `scrape-${result.domain || 'site'}-${Date.now()}.json`;
    a.click();
  };

  return (
    <div data-testid="scrape-live-view" style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: 'radial-gradient(ellipse at top, #0F0F15 0%, #06060A 100%)',
      color: '#fff', overflow: 'hidden', position: 'relative',
    }}>
      {/* Grid backdrop */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(6,182,212,0.04) 1px, transparent 1px),
          linear-gradient(90deg, rgba(6,182,212,0.04) 1px, transparent 1px)
        `,
        backgroundSize: '42px 42px', opacity: 0.6,
        animation: 'grid-drift 26s linear infinite',
      }} />

      {/* Top bar */}
      <div style={{
        position: 'relative', zIndex: 2,
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '12px 20px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(8,8,14,0.85)', backdropFilter: 'blur(14px)',
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 6,
          background: 'rgba(6,182,212,0.12)',
          border: '1px solid rgba(6,182,212,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Search size={15} color="#06B6D4" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#06B6D4', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Kai · Scraping Expert
          </div>
          <div style={{
            fontSize: 13, fontWeight: 700, marginTop: 2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            maxWidth: 460,
          }}>
            {url}
          </div>
        </div>

        <div style={{ flex: 1 }} />

        {/* Elapsed timer */}
        <div style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600,
          padding: '6px 10px',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.06)',
          color: 'rgba(255,255,255,0.65)',
        }}>
          {elapsed.toFixed(1)}s
        </div>

        {status === 'done' && (
          <button onClick={exportJSON} data-testid="scrape-export-btn" style={iconBtn}>
            <Download size={14} />
          </button>
        )}
        {onClose && (
          <button onClick={onClose} data-testid="scrape-close-btn" style={iconBtn}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative', zIndex: 2 }}>
        {status !== 'done' && status !== 'error' && (
          <RunningStage
            progress={progress}
            stepLabel={stepLabel}
            metrics={metrics}
            logs={logs}
            capturedImages={capturedImages}
            overview={overview}
            techStack={techStack}
            palette={palette}
            logsRef={logsRef}
          />
        )}
        {status === 'error' && <ErrorStage error={error} onClose={onClose} />}
        {status === 'done' && result && (
          <ResultStage
            result={result}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        )}
      </div>
    </div>
  );
}

/* ============ RUNNING ============ */
function RunningStage({ progress, stepLabel, metrics, logs, capturedImages, overview, techStack, palette, logsRef }) {
  return (
    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.2fr 1fr', overflow: 'hidden' }}>
      {/* Left: gauge + metrics */}
      <div style={{ padding: '28px 32px', overflowY: 'auto', borderRight: '1px solid rgba(255,255,255,0.04)' }}>
        {/* Big gauge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 28, marginBottom: 28 }}>
          <CircularGauge value={progress} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>
              Current task
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>
              {stepLabel}
            </div>
            <div style={{
              marginTop: 14, height: 3, background: 'rgba(255,255,255,0.05)', position: 'relative', overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', top: 0, left: 0, bottom: 0,
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #06B6D4, #22D3EE)',
                transition: 'width 0.4s ease',
                boxShadow: '0 0 12px rgba(6,182,212,0.5)',
              }} />
            </div>
          </div>
        </div>

        {/* Metrics grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
          <Metric icon={<Zap size={12} />} label="Status" value={metrics.status || '—'} color="#22C55E" />
          <Metric icon={<FileText size={12} />} label="Size" value={`${metrics.size_kb || 0} KB`} color="#A855F7" />
          <Metric icon={<Type size={12} />} label="Words" value={metrics.words || 0} color="#FFB020" />
          <Metric icon={<ImageIcon size={12} />} label="Images" value={metrics.images || 0} color="#06B6D4" live />
          <Metric icon={<Link2 size={12} />} label="Links" value={(metrics.links_internal || 0) + (metrics.links_external || 0)} color="#22D3EE" live />
          <Metric icon={<Cpu size={12} />} label="Scripts" value={metrics.scripts || 0} color="#EF4444" />
        </div>

        {/* Overview card — appears when data ready */}
        {overview && (
          <div style={{
            padding: 16, background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            marginBottom: 16, animation: 'fadeIn 0.3s ease-out',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              {overview.favicon
                ? <img src={overview.favicon} alt="" style={{ width: 18, height: 18 }} />
                : <Globe size={16} color="#06B6D4" />}
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{overview.domain}</div>
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{overview.title}</div>
            {overview.description && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
                {overview.description}
              </div>
            )}
          </div>
        )}

        {/* Captured images strip */}
        {capturedImages.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={labelSmall}>Capturing images · {capturedImages.length}</div>
            <div style={{
              display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6,
              scrollbarWidth: 'thin',
            }}>
              {capturedImages.slice(-12).map((img, i) => (
                <img
                  key={i}
                  src={img.src}
                  alt=""
                  referrerPolicy="no-referrer"
                  style={{
                    width: 60, height: 60, objectFit: 'cover', flexShrink: 0,
                    border: '1px solid rgba(255,255,255,0.06)',
                    animation: 'fadeIn 0.3s ease-out',
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ))}
            </div>
          </div>
        )}

        {techStack.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={labelSmall}>Tech stack detected</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {techStack.map(t => (
                <span key={t} style={chipStyle('#06B6D4')}>{t}</span>
              ))}
            </div>
          </div>
        )}

        {palette.colors.length > 0 && (
          <div>
            <div style={labelSmall}>Palette sample</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {palette.colors.map(c => (
                <div key={c} title={c} style={{
                  width: 22, height: 22, background: c,
                  border: '1px solid rgba(255,255,255,0.08)',
                }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Right: live terminal logs */}
      <div style={{
        display: 'flex', flexDirection: 'column',
        background: '#05050A',
      }}>
        <div style={{
          padding: '10px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          fontSize: 10, fontWeight: 700, color: 'rgba(6,182,212,0.8)',
          letterSpacing: '0.12em', textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: '#06B6D4', boxShadow: '0 0 8px #06B6D4',
            animation: 'pulse-dot 1s ease-in-out infinite',
          }} />
          Live stream
        </div>
        <div
          ref={logsRef}
          style={{
            flex: 1, overflowY: 'auto', padding: 16,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11, lineHeight: 1.7,
          }}
        >
          {logs.map((l, i) => (
            <div key={i} style={{
              color: 'rgba(255,255,255,0.7)',
              animation: 'fadeIn 0.25s ease-out',
            }}>
              <span style={{ color: 'rgba(255,255,255,0.3)', marginRight: 8 }}>
                {new Date(l.ts).toLocaleTimeString('en-US', { hour12: false })}
              </span>
              {l.line}
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes grid-drift { from { background-position: 0 0; } to { background-position: 42px 42px; } }
        @keyframes pulse-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}

/* ============ RESULT ============ */
function ResultStage({ result, activeTab, setActiveTab }) {
  const tabs = [
    { key: 'overview', label: 'Overview', icon: Globe },
    { key: 'cards', label: `Cards (${(result.cards || []).length})`, icon: FileText },
    { key: 'content', label: `Content (${(result.headings || []).length})`, icon: FileText },
    { key: 'images', label: `Images (${result.images_count})`, icon: ImageIcon },
    { key: 'links', label: `Links (${result.links_internal + result.links_external})`, icon: Link2 },
    { key: 'tech', label: 'Tech', icon: Cpu },
  ];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 0,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '0 20px',
      }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            data-testid={`scrape-tab-${t.key}`}
            style={{
              padding: '14px 16px', background: 'transparent', border: 'none',
              color: activeTab === t.key ? '#06B6D4' : 'rgba(255,255,255,0.5)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
              borderBottom: activeTab === t.key ? '2px solid #06B6D4' : '2px solid transparent',
              transition: 'all 0.2s',
            }}
          >
            <t.icon size={13} />
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        {activeTab === 'overview' && <OverviewTab r={result} />}
        {activeTab === 'cards' && <CardsTab r={result} />}
        {activeTab === 'content' && <ContentTab r={result} />}
        {activeTab === 'images' && <ImagesTab r={result} />}
        {activeTab === 'links' && <LinksTab r={result} />}
        {activeTab === 'tech' && <TechTab r={result} />}
      </div>
    </div>
  );
}

function OverviewTab({ r }) {
  const kpis = [
    { label: 'HTTP status', value: r.status, color: r.status < 400 ? '#22C55E' : '#EF4444' },
    { label: 'Page size', value: `${r.size_kb} KB`, color: '#A855F7' },
    { label: 'Word count', value: r.word_count.toLocaleString(), color: '#FFB020' },
    { label: 'Images', value: r.images_count, color: '#06B6D4' },
    { label: 'Internal links', value: r.links_internal, color: '#22D3EE' },
    { label: 'External links', value: r.links_external, color: '#EC4899' },
    { label: 'Headings', value: r.headings_count, color: '#7C3AED' },
    { label: 'Scripts', value: r.scripts_count, color: '#EF4444' },
  ];
  return (
    <div>
      {/* Hero */}
      <div style={{
        display: 'flex', gap: 20, marginBottom: 28,
        padding: 24, background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        {r.og_image && (
          <img src={r.og_image} alt="" referrerPolicy="no-referrer"
            style={{ width: 160, height: 100, objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(255,255,255,0.06)' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            {r.favicon && <img src={r.favicon} alt="" style={{ width: 16, height: 16 }} referrerPolicy="no-referrer" />}
            <a href={r.final_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#06B6D4', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
              {r.domain} <ExternalLink size={11} />
            </a>
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, lineHeight: 1.3 }}>{r.title}</div>
          {r.description && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
              {r.description}
            </div>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 28 }}>
        {kpis.map(k => (
          <div key={k.label} style={{
            padding: 16, background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.05)',
          }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {k.label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: k.color, marginTop: 6 }}>
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Tech + palette in side-by-side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Section title="Tech stack" icon={<Cpu size={12} />}>
          {r.tech_stack.length === 0 ? <Empty /> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {r.tech_stack.map(t => <span key={t} style={chipStyle('#06B6D4')}>{t}</span>)}
            </div>
          )}
        </Section>
        <Section title="Palette" icon={<Palette size={12} />}>
          {r.colors.length === 0 ? <Empty /> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {r.colors.map(c => (
                <div key={c} title={c} style={{
                  width: 28, height: 28, background: c,
                  border: '1px solid rgba(255,255,255,0.06)',
                }} />
              ))}
            </div>
          )}
        </Section>
      </div>
      {r.fonts.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <Section title="Fonts" icon={<Type size={12} />}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {r.fonts.map(f => <span key={f} style={chipStyle('#A855F7')}>{f}</span>)}
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}

function CardsTab({ r }) {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState({}); // index -> bool
  const cards = r.cards || [];
  if (!cards.length) return <Empty />;
  const filtered = !filter.trim()
    ? cards
    : cards.filter(c => (c.title + ' ' + (c.description || '')).toLowerCase().includes(filter.toLowerCase()));

  const toggleExpand = (i) => setExpanded(s => ({ ...s, [i]: !s[i] }));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <input
          type="text"
          placeholder={`Filter ${cards.length} cards...`}
          value={filter}
          onChange={e => setFilter(e.target.value)}
          data-testid="scrape-cards-filter"
          style={{
            flex: 1, padding: '10px 14px',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: '#fff', fontSize: 12, fontFamily: 'inherit', outline: 'none',
          }}
        />
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
          {filtered.length} / {cards.length}
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {filtered.slice(0, 2500).map((c, i) => {
          const isExpanded = !!expanded[i];
          const hasLongDesc = (c.description || '').length > 140;
          return (
          <div
            key={i}
            data-testid={`scrape-card-${i}`}
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              color: '#fff',
              transition: 'all 0.15s', overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(6,182,212,0.4)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'}
          >
            {c.image && (
              <img src={c.image} alt="" referrerPolicy="no-referrer" style={{
                width: '100%', height: 140, objectFit: 'cover', display: 'block', background: '#0F0F15',
              }} onError={(e) => { e.target.style.display = 'none'; }} />
            )}
            <div style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, lineHeight: 1.3 }}>
                {c.title || '(untitled)'}
              </div>
              {c.description && (
                <div style={{
                  fontSize: 11, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55,
                  marginBottom: 10, flex: 1,
                  ...(isExpanded ? {} : {
                    display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }),
                }}>
                  {c.description}
                </div>
              )}
              {hasLongDesc && (
                <button
                  onClick={() => toggleExpand(i)}
                  style={{
                    background: 'none', border: 'none', color: '#06B6D4',
                    fontSize: 10, fontWeight: 600, padding: 0, marginBottom: 8,
                    cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                  }}
                >
                  {isExpanded ? '− Show less' : '+ Show more'}
                </button>
              )}
              {c.href && (
                <a
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    fontSize: 10, color: '#06B6D4', textDecoration: 'none',
                    padding: '6px 10px',
                    background: 'rgba(6,182,212,0.08)',
                    border: '1px solid rgba(6,182,212,0.2)',
                    marginTop: 'auto',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(6,182,212,0.15)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(6,182,212,0.08)'}
                >
                  <ExternalLink size={10} /> {c.href.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>
          </div>
        );})}
      </div>
      {filtered.length > 2500 && (
        <div style={{ textAlign: 'center', padding: 18, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
          Showing 2500 of {filtered.length} cards
        </div>
      )}
    </div>
  );
}

function ContentTab({ r }) {
  if (!r.headings?.length) return <Empty />;  const byLevel = { h1: 0, h2: 0, h3: 0 };
  r.headings.forEach(h => { byLevel[h.level] = (byLevel[h.level] || 0) + 1; });
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
        {['h1', 'h2', 'h3'].map(lvl => (
          <div key={lvl} style={{ padding: '8px 14px', border: '1px solid rgba(255,255,255,0.06)', fontSize: 12 }}>
            <span style={{ color: 'rgba(255,255,255,0.4)', marginRight: 8 }}>{lvl.toUpperCase()}</span>
            <span style={{ color: '#06B6D4', fontWeight: 700 }}>{byLevel[lvl]}</span>
          </div>
        ))}
      </div>
      <div>
        {r.headings.map((h, i) => (
          <div key={i} style={{
            padding: '10px 14px', marginBottom: 4,
            borderLeft: `2px solid ${h.level === 'h1' ? '#06B6D4' : h.level === 'h2' ? '#22D3EE' : 'rgba(255,255,255,0.2)'}`,
            paddingLeft: h.level === 'h1' ? 14 : h.level === 'h2' ? 26 : 38,
            background: 'rgba(255,255,255,0.02)',
            fontSize: h.level === 'h1' ? 14 : 12,
            fontWeight: h.level === 'h1' ? 700 : 500,
            color: h.level === 'h1' ? '#fff' : 'rgba(255,255,255,0.7)',
          }}>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginRight: 10 }}>
              {h.level.toUpperCase()}
            </span>
            {h.text}
          </div>
        ))}
      </div>
    </div>
  );
}

function ImagesTab({ r }) {
  if (!r.images?.length) return <Empty />;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
      {r.images.map((img, i) => (
        <div key={i} style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.06)',
          overflow: 'hidden',
        }}>
          <img src={img.src} alt="" referrerPolicy="no-referrer" style={{
            width: '100%', height: 140, objectFit: 'cover', display: 'block', background: '#0F0F15',
          }} onError={(e) => { e.target.style.opacity = 0.2; e.target.src = ''; }} />
          {img.alt && (
            <div style={{ padding: '6px 10px', fontSize: 10, color: 'rgba(255,255,255,0.5)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {img.alt}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function LinksTab({ r }) {
  if (!r.links?.length) return <Empty />;
  return (
    <div>
      {r.links.map((l, i) => (
        <a
          key={i}
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 12px', marginBottom: 4,
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.04)',
            color: '#fff', textDecoration: 'none', fontSize: 12,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(6,182,212,0.3)'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.04)'}
        >
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
            padding: '2px 6px',
            color: l.kind === 'internal' ? '#22C55E' : '#EC4899',
            background: l.kind === 'internal' ? 'rgba(34,197,94,0.1)' : 'rgba(236,72,153,0.1)',
            border: `1px solid ${l.kind === 'internal' ? 'rgba(34,197,94,0.2)' : 'rgba(236,72,153,0.2)'}`,
          }}>
            {l.kind === 'internal' ? 'INT' : 'EXT'}
          </span>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {l.text}
          </span>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)',
            maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {l.href}
          </span>
          <ExternalLink size={11} color="rgba(255,255,255,0.3)" />
        </a>
      ))}
    </div>
  );
}

function TechTab({ r }) {
  return (
    <div>
      <Section title="Detected technologies" icon={<Cpu size={12} />}>
        {r.tech_stack.length === 0 ? <Empty /> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {r.tech_stack.map(t => (
              <div key={t} style={{
                padding: '14px 16px',
                background: 'rgba(6,182,212,0.05)',
                border: '1px solid rgba(6,182,212,0.15)',
                fontSize: 13, fontWeight: 600,
              }}>
                {t}
              </div>
            ))}
          </div>
        )}
      </Section>
      <div style={{ marginTop: 16, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
        {r.scripts_count} external scripts loaded · {r.fonts.length} font families
      </div>
    </div>
  );
}

function ErrorStage({ error, onClose }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
      <div style={{
        maxWidth: 400, padding: 28, textAlign: 'center',
        background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)',
      }}>
        <AlertCircle size={36} color="#EF4444" style={{ margin: '0 auto 14px', display: 'block' }} />
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Scrape failed</div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 20 }}>{error}</div>
        {onClose && (
          <button onClick={onClose} style={{
            padding: '8px 18px', background: '#EF4444', color: '#fff', border: 'none',
            fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Close
          </button>
        )}
      </div>
    </div>
  );
}

/* ============ SHARED PIECES ============ */
function CircularGauge({ value }) {
  const size = 150;
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circum = 2 * Math.PI * radius;
  const offset = circum - (value / 100) * circum;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none"
          stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none"
          stroke="url(#scrapeGrad)" strokeWidth={stroke}
          strokeDasharray={circum}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.5s ease', filter: 'drop-shadow(0 0 8px rgba(6,182,212,0.5))' }} />
        <defs>
          <linearGradient id="scrapeGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#06B6D4" />
            <stop offset="100%" stopColor="#22D3EE" />
          </linearGradient>
        </defs>
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 32, fontWeight: 800, color: '#06B6D4',
          lineHeight: 1,
        }}>
          {Math.round(value)}
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', marginTop: 4 }}>
          PERCENT
        </div>
      </div>
    </div>
  );
}

function Metric({ icon, label, value, color, live }) {
  return (
    <div style={{
      padding: '12px 14px',
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.05)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 10, color: 'rgba(255,255,255,0.45)',
        letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>
        <span style={{ color }}>{icon}</span>
        {label}
        {live && <span style={{
          marginLeft: 'auto', width: 5, height: 5, borderRadius: '50%',
          background: color, boxShadow: `0 0 6px ${color}`,
          animation: 'pulse-dot 1s ease-in-out infinite',
        }} />}
      </div>
      <div style={{
        fontSize: 20, fontWeight: 800, color: '#fff', marginTop: 4,
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        {value}
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div style={{
      padding: 16, background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.05)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 10, color: 'rgba(255,255,255,0.4)',
        letterSpacing: '0.08em', textTransform: 'uppercase',
        marginBottom: 10,
      }}>
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

function Empty() {
  return <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', padding: 12 }}>No data</div>;
}

const chipStyle = (color) => ({
  fontSize: 10, padding: '4px 10px',
  color,
  background: `${color}15`,
  border: `1px solid ${color}30`,
  fontWeight: 600,
});

const labelSmall = {
  fontSize: 10, color: 'rgba(255,255,255,0.4)',
  letterSpacing: '0.08em', textTransform: 'uppercase',
  marginBottom: 8,
};

const iconBtn = {
  width: 30, height: 30,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.06)',
  color: 'rgba(255,255,255,0.6)',
  cursor: 'pointer', fontFamily: 'inherit',
};
